/**
 * Estado do extrato na tela: filtros, ordenacao, tamanho e deslocamento da
 * pagina. A consulta que vai ao servidor e montada a partir daqui.
 *
 * Decisoes nao obvias:
 *  1. O estado mora na PAGINA (nao na tabela) para cartoes, "O que fazer
 *     agora" e o bloco do comercial abrirem o extrato ja filtrado e rolarem
 *     ate ele. Mudar qualquer filtro ou a ordem volta para a primeira pagina.
 *  2. Ordem padrao: pagamento mais recente primeiro, igual ao servidor.
 *     Clicar numa coluna numerica comeca decrescente (o maior primeiro e a
 *     pergunta usual); texto comeca crescente.
 *  3. Cada cabecalho tem um `titulo` em termos de negocio, que vira tooltip:
 *     "% aplicada" sozinho nao diz que o percentual e o CONGELADO na venda.
 */

import type { ConversionFilters, SortColumn } from './types';

export const TAMANHOS_DE_PAGINA: readonly number[] = [20, 50, 100];

export interface EstadoDoExtrato extends ConversionFilters {
  readonly sort: SortColumn;
  readonly desc: boolean;
  readonly limit: number;
  readonly offset: number;
}

export const FILTROS_VAZIOS: ConversionFilters = {
  channel: 'todos',
  status: 'todos',
  closedBy: 'todos',
  refund: 'todos',
  amount: 'todas',
  search: '',
};

export const ESTADO_INICIAL_DO_EXTRATO: EstadoDoExtrato = { ...FILTROS_VAZIOS, sort: 'paidAt', desc: true, limit: 20, offset: 0 };

export interface ColunaDoExtrato {
  readonly rotulo: string;
  readonly numerica: boolean;
  readonly titulo: string;
}

export const COLUNAS_DO_EXTRATO: Readonly<Record<SortColumn, ColunaDoExtrato>> = {
  paidAt: { rotulo: 'Pagamento', numerica: false, titulo: 'Quando o lead pagou (ou a vendedora informou o pagamento, na venda assistida)' },
  company: { rotulo: 'Empresa', numerica: false, titulo: 'Empresa dona da venda recuperada' },
  amount: { rotulo: 'Valor pago', numerica: true, titulo: 'Valor efetivamente pago pelo lead: é a base da comissão (ADR-001)' },
  fee: { rotulo: '% aplicada', numerica: true, titulo: 'Percentual congelado no momento da venda. Mudar o percentual da empresa não reescreve esta linha' },
  commission: { rotulo: 'Comissão', numerica: true, titulo: 'Comissão do Converte Fácil sobre esta venda, congelada. Riscada quando o estorno a reverteu' },
  contact: { rotulo: 'Contato creditado', numerica: false, titulo: 'Último toque do motor antes do pagamento, em até 7 dias: é quem recebe o crédito da venda' },
  closedBy: { rotulo: 'Fechado por', numerica: false, titulo: 'Vendedora do comercial que fechou depois que o lead respondeu ao toque. Informação adicional: o crédito continua do toque (ADR-001 E5)' },
  status: { rotulo: 'Status', numerica: false, titulo: 'A faturar, faturada, paga ou revertida; estorno recebido aparece abaixo, mesmo quando a comissão foi mantida' },
};

export const ORDEM_DAS_COLUNAS: readonly SortColumn[] = ['paidAt', 'company', 'amount', 'fee', 'commission', 'contact', 'closedBy', 'status'];

export function alternarOrdem(estado: EstadoDoExtrato, coluna: SortColumn): Pick<EstadoDoExtrato, 'sort' | 'desc'> {
  if (estado.sort === coluna) {
    return { sort: coluna, desc: !estado.desc };
  }
  const numerica = COLUNAS_DO_EXTRATO[coluna].numerica || coluna === 'paidAt';
  return { sort: coluna, desc: numerica };
}

/** Quantos filtros estao fora do padrao (a busca conta como um). */
export function contarFiltrosAtivos(filtros: ConversionFilters): number {
  return (
    (filtros.channel === 'todos' ? 0 : 1) +
    (filtros.status === 'todos' ? 0 : 1) +
    (filtros.closedBy === 'todos' ? 0 : 1) +
    (filtros.refund === 'todos' ? 0 : 1) +
    (filtros.amount === 'todas' ? 0 : 1) +
    (filtros.search.trim() === '' ? 0 : 1)
  );
}

/** Ciclo do botao "Exportar CSV": o arquivo sai do recorte inteiro, nao da pagina. */
export type EstadoDaExportacao =
  | { readonly status: 'parado' }
  | { readonly status: 'gerando' }
  | { readonly status: 'pronto'; readonly linhas: number; readonly total: number; readonly truncado: boolean }
  | { readonly status: 'erro'; readonly mensagem: string };
